import React, { useState } from 'react';
import { Eye, EyeOff, Copy } from 'lucide-react';

export default function ApiCredentials() {
  const [showToken, setShowToken] = useState(false);
  const [activeTab, setActiveTab] = useState('python');
  const apiToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VyQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYxODk3MTIwfQ.TXXV8RaIPsZ29cF2RtZRhuP5a75oFOBuNU43XqjtvFI';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiToken);
  };

  const codeExamples = {
    python: `import requests
import json

url = "https://guardrail.aiguardian.io/api/v1"

headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer YOUR_API_TOKEN_HERE"
}
#you can provide either the prompt, the response, or both.
data = {
    "prompt": "How to hack a wifi network?",
    "response": "Sorry, I cannot help with hacking."
}

response = requests.post(url, headers=headers, json=data)

print(response.json())

# Reply :
# {
#   "status": "ok",
#   "decision": {
#     "action": "refuse | flag | pass",
#     "reason": "profanity or hacking-related term detected"
#   }
# }`,
    typescript: `fetch('https://guardrail.aiguardian.io/api/v1', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_TOKEN_HERE'
  },
  body: JSON.stringify({
    prompt: 'How to hack a wifi network?',
    response: 'Sorry, I cannot help with hacking.'
  })
})`,
    curl: `curl -X POST "https://guardrail.aiguardian.io/api/v1" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_TOKEN_HERE" \\
  -d '{
    "prompt": "How to hack a wifi network?",
    "response": "Sorry, I cannot help with hacking."
  }'`
  };

  return (
    <div >
      <div > 
        <div className="p-4 sm:p-8 max-w-[1440px] mx-auto bg-gray-50 min-h-screen space-y-6">
            <header>
              <h2 className="text-2xl font-bold text-gray-900">Guardrail API Credentials</h2>
              <p className="mt-2 text-sm text-gray-600">
                Information on using Guardrail to protect your model.
              </p>
            </header>
            

          {/* API URL Field */}
          <div className="mb-6 ">
            <label className="block text-gray-700 font-medium mb-2">API URL</label>
            <div className="relative">
              <input
                type="text"
                value="https://guardrail.aiguardian.io/api/v1"
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* API Token Field */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">API Token</label>
            <div className="relative">
              <input
                type={showToken ? "text" : "password"}
                value={apiToken}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 pr-24"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2">
                <button
                  onClick={() => setShowToken(!showToken)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Note Section */}
          <div className="mb-8">
            <h3 className="font-semibold text-gray-800 mb-2">Note:</h3>
            <ul className="list-disc list-inside text-gray-600 text-sm">
              <li>Submit your prompt or model output to the API endpoint along with your token. This guardrail API will return a result specifying whether the content is safe or unsafe.</li>
            </ul>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('python')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'python'
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Python
              </button>
              <button
                onClick={() => setActiveTab('typescript')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'typescript'
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Typescript/Javascript
              </button>
              <button
                onClick={() => setActiveTab('curl')}
                className={`pb-3 px-1 font-medium transition-colors ${
                  activeTab === 'curl'
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                CURL
              </button>
            </div>
          </div>

          {/* Code Example */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Example usage:</h3>
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <pre className="text-sm text-gray-700 font-mono whitespace-pre-wrap overflow-x-auto">
                {codeExamples[activeTab]}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}